/*
 * IMPORTS
 */
import React from 'react' // Npm: react.js library.
import PropTypes from 'prop-types' // Npm: react.js library.
import JoiBrowser from 'joi-browser' // Npm: Joi for frontend validation.
import _ from 'underscore' // Npm: Underscore.js library.
import { toast } from 'react-hot-toast' // Npm: React hot toast.
import { connect } from 'react-redux' // Npm: React Redux for state management.
import { useLazyQuery, useMutation } from '@apollo/client' // Npm: Apollo client.


/*
 * PACKAGES
 */
import SubmitButton from 'components/SubmitButton'
import { MemoizedInput, MemoizedSelect } from 'components/MemoizedInput'
import CustomerAccountSelector from 'components/CustomerAccountSelector'


/*
 * GRAPHS
 */
import SenderRouteReadQuery from './__query__/index.senderroute.read.query'
import SenderRouteUpdateMutation from './__mutation__/index.senderroute.update.mutation'
import SenderRouteCreateMutation from './__mutation__/index.senderroute.create.mutation'


/*
 * OBJECTS
 */
const Index = ({ isOpen, isCreateOnly, onClose, type, passOn, containerStyle, refetch }) => {
  // Hook assignment.
  const [error, setError] = React.useState('')
  const [formVersion, _ResetFormInputs] = React.useReducer(j => j + 1, 0)
  const [customerAccountId, setCustomerAccountId] = React.useState('')
  const [QuerySenderRouteRead, QuerySenderRouteReadResponse] = useLazyQuery(SenderRouteReadQuery, { 'fetchPolicy': Object.React.App.fetchPolicy })
  const [MutationSenderRouteCreate, MutationSenderRouteCreateResponse] = useMutation(SenderRouteCreateMutation)
  const [MutationSenderRouteUpdate, MutationSenderRouteUpdateResponse] = useMutation(SenderRouteUpdateMutation)
  const _formDataRef = React.useRef({ type })

  // Object assignment.
  const _SubmitForm = async e => {
    // Prevent default behavior.
    e.preventDefault()

    // Reset error.
    setError('')

    // Update _formDataRef with type
    _formDataRef.current = { ..._formDataRef.current, 'type': type }

    // Const assignment.
    const _JoiSchema = JoiBrowser.object({
      'displayName': JoiBrowser.string().required(),
      'type': JoiBrowser.string().required(),
      'customerAccountId': JoiBrowser.string().required()
    }).options({ 'allowUnknown': true })

    // Remove all keys from _formDataRef.current which are undefined.
    _formDataRef.current = _.pick(_formDataRef.current, _.identity)

    // Validate form data.
    const _JoiSchemaValidate = _JoiSchema.validate(_formDataRef.current)

    // If error exists then report failure.
    if (_JoiSchemaValidate.error) return setError(_JoiSchemaValidate.error?.message)

    /*
     * Push routeId if not found on the _formDataRef.
     * also make sure that isCreateOnly is false.
     */
    if (!isCreateOnly && _.isEmpty(_formDataRef.current?.routeId)) _formDataRef.current = { ..._formDataRef.current, 'routeId': passOn?.routeId }

    // Execute update mutation.
    const _MutationSenderRouteUpdate = await [isCreateOnly ? MutationSenderRouteCreate : MutationSenderRouteUpdate]?.[0]({ 'variables': _.omit(_formDataRef?.current, isCreateOnly ? 'routeId' : void 0) })

    // If mutation caught an exception then report failure.
    if (_MutationSenderRouteUpdate instanceof Error) return _MutationSenderRouteUpdate

    // Style Guide.
    toast(_MutationSenderRouteUpdate?.data?.SenderRouteUpdate?.message ?? _MutationSenderRouteUpdate?.data?.SenderRouteCreate?.message)

    // Execute onClose only if response is successful.
    if ('UPDATE_SUCCESSFUL' === _MutationSenderRouteUpdate?.data?.SenderRouteUpdate?.status || 'CREATE_SUCCESSFUL' === _MutationSenderRouteUpdate?.data?.SenderRouteCreate?.status) {
      // Execute refetch.
      refetch?.()

      // Report success.
      return onClose?.()
    }

    // Report void 0.
    return void 0
  }

  // Event handler.
  React.useEffect(() => {
    // Only prepare the form while the modal is open.
    if (!isOpen) return

    // Create mode should reset local form state without hitting read query.
    if (isCreateOnly) {
      _formDataRef.current = { type }
      setCustomerAccountId('')
      setError('')
      _ResetFormInputs()
      return
    }

    if (_.isEmpty(passOn?.routeId)) return

    let _isActive = true

    // _Async handler.
    const _Async = async () => {
      // Const assignment.
      const _QuerySenderRouteReadQuery = await QuerySenderRouteRead({ 'variables': { 'routeId': passOn?.routeId } })

      if (!_isActive) return void 0

      // If query caught an exception then report failure.
      if (_QuerySenderRouteReadQuery instanceof Error) return _QuerySenderRouteReadQuery

      /*
       * If route details fetch complete then
       * update its value.
       */
      if (0 < _QuerySenderRouteReadQuery?.data?.SenderRouteRead?.length) {
        // Const assignment.
        const _data = _.first(_QuerySenderRouteReadQuery?.data?.SenderRouteRead)

        // Extract customer account from nested CustomerAccount array.
        const customerAccount = _.first(_data.CustomerAccount)

        // Update form data.
        _formDataRef.current = {
          'routeId': _data.id,
          'displayName': _data.displayName,
          'type': _data.type,
          'customerAccountId': customerAccount?.id || ''
        }

        // Build display value from CustomerAccount for UI rendering.
        const displayValue = customerAccount?.displayName && customerAccount?.id
          ? `${customerAccount.displayName} (${customerAccount.id})`
          : customerAccount?.id || ''

        // Update state with formatted display value.
        setCustomerAccountId(displayValue)

        // Reset uncontrolled inputs once loaded data is ready.
        return _ResetFormInputs()
      }

      // Report failure.
      return void 0
    }; _Async()

    return () => {
      _isActive = false
    }
  }, [QuerySenderRouteRead, _ResetFormInputs, isCreateOnly, isOpen, passOn?.routeId, type])

  // Component assignment.
  const _isLoading = MutationSenderRouteUpdateResponse.loading || MutationSenderRouteCreateResponse.loading
  const _isInputDisabled = isCreateOnly ? false : _isLoading || QuerySenderRouteReadResponse.loading

  // Return component.
  return (
    <form style={containerStyle} onSubmit={_SubmitForm} key={formVersion}>
      <MemoizedInput
        disabled={_isInputDisabled}
        name='displayName'
        label='Name'
        placeholder='e.g. "US Route"'
        onChange={({ target }) => {
          // Over spreading.
          const { name, value } = target

          // Update form data.
          _formDataRef.current = {
            ..._formDataRef?.current,
            [name]: value
          }
        }}
        error={error}
        isInvalid={error?.includes('displayName')}
        data={_formDataRef?.current?.displayName}
      />
      <MemoizedSelect
        disabled={isCreateOnly || _isInputDisabled}
        name='type'
        label='Type'
        placeholder='e.g. "WHOLESALE"'
        iconColor='brand.500'
        onChange={({ target }) => {
          // Over spreading.
          const { name, value } = target

          // Update form data.
          _formDataRef.current = {
            ..._formDataRef?.current,
            [name]: value
          }
        }}
        error={error}
        options={Object.React.App.enums.CUSTOMER_TYPE.enums?.map(i => i.key)}
        isInvalid={error?.includes('type')}
        data={isCreateOnly ? type : _formDataRef?.current?.type}
      />

      <CustomerAccountSelector
        name='customerAccountId'
        customerType={type}
        label='Customer Account'
        placeholder='e.g. Spam Customer Account'
        iconColor='brand.500'
        error={error}
        isInvalid={error?.includes('customerAccountId')}
        onChange={({ target }) => {
          // Destructure name and value from target
          const { name, value } = target

          // Extract only ID from "Name (id)" format for backend submission
          const extractedId = value?.match(/\(([^)]+)\)/)?.[1] || value

          // Store extracted ID in form ref for backend mutation
          _formDataRef.current = {
            ..._formDataRef?.current,
            [name]: extractedId
          }

          // Update state with full display value for UI rendering
          setCustomerAccountId(value)
        }}
        disabled={!isCreateOnly || _isInputDisabled}
        data={customerAccountId}
      />
      <SubmitButton
        margin={0}
        disabled={_isInputDisabled}
        onSubmit={_SubmitForm}
        defaultText={isCreateOnly ? 'Create Sender Route' : 'Update Sender Route'}
        isLoading={_isLoading} />
    </form>
  )
}


/*
 * PROPTYPES
 */
Index.propTypes = {
  'isCreateOnly': PropTypes.bool,
  'isOpen': PropTypes.bool,
  'onClose': PropTypes.func,
  'passOn': PropTypes.object,
  'type': PropTypes.string,
  'containerStyle': PropTypes.object,
  'refetch': PropTypes.func
}
Index.defaultProps = {
  'containerStyle': {
    'width': '100%',
    'display': 'flex',
    'flexDirection': 'column',
    'gap': '22px'
  }
}


/*
 * REDUX
 */
const _MapStateToProps = __state => ({ 'passOn': __state.PassOn })


/*
 * EXPORT
 */
export default connect(_MapStateToProps)(React.memo(Index))
